import { useCallback, useMemo, useRef, useState } from "react";

import type { JourneyMode } from "@/data/schema/compiled";
import {
  buildBookTimelineLayout,
  type BookTimelineLayout,
} from "@/lib/timeline/book/BookLayout";
import { withoutSecretPresence } from "@/data/secret-presence";
import { journeyModel, type BookJourneyModel } from "@/pages/loadBookTimeline";
import {
  claimPendingScene,
  shareLoad,
  type PendingScene,
  type StoryMoment,
} from "./universeJourneyLoad";

export function useUniverseJourney(
  book: BookJourneyModel,
  enabled: boolean,
  loadUniverse: (() => Promise<BookJourneyModel>) | undefined,
  initialUniverse?: BookJourneyModel,
  spoilersShown = false,
): {
  journey: JourneyMode;
  active: BookJourneyModel;
  layout: BookTimelineLayout;
  loading: boolean;
  failed: boolean;
  prefetch: () => void;
  requestJourney: (next: JourneyMode, moment: StoryMoment | undefined) => void;
  hasPendingScene: () => boolean;
  keepMoment: (moment: StoryMoment | undefined) => void;
  takePendingMoment: () => { moment: StoryMoment | undefined } | undefined;
} {
  const [journey, setJourney] = useState<JourneyMode>(
    initialUniverse === undefined ? "book" : "universe",
  );
  const [universe, setUniverse] = useState<BookJourneyModel | null>(
    initialUniverse ?? null,
  );
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const pendingScene = useRef<PendingScene | null>(null);
  const wanted = useRef<JourneyMode>(journey);
  const loader = useMemo(
    () => (loadUniverse === undefined ? undefined : shareLoad(loadUniverse)),
    [loadUniverse],
  );

  const prefetch = useCallback((): void => {
    if (!enabled || universe !== null || loader === undefined) {
      return;
    }
    // A failed prefetch stays quiet. The click retries and shows the failure.
    void loader().then(setUniverse).catch(() => undefined);
  }, [enabled, universe, loader]);

  const requestJourney = useCallback((
    next: JourneyMode,
    moment: StoryMoment | undefined,
  ): void => {
    if (!enabled) {
      return;
    }
    wanted.current = next;
    setFailed(false);
    if (next === journey) {
      pendingScene.current = null;
      setLoading(false);
      return;
    }
    pendingScene.current = { journey: next, moment };
    if (next === "universe" && universe === null) {
      if (loader === undefined) {
        return;
      }
      setLoading(true);
      void loader()
        .then((model) => {
          setUniverse(model);
          if (wanted.current === "universe") {
            setJourney("universe");
          }
          setLoading(false);
        })
        .catch((error: unknown) => {
          console.error("Universe view failed to load.", error);
          if (wanted.current === "universe") {
            wanted.current = "book";
            pendingScene.current = null;
            setFailed(true);
          }
          setLoading(false);
        });
      return;
    }
    setJourney(next);
  }, [enabled, journey, universe, loader]);

  const hasPendingScene = (): boolean => pendingScene.current?.journey === journey;

  const takePendingMoment = (): { moment: StoryMoment | undefined } | undefined =>
    claimPendingScene(pendingScene, journey);

  /** For a rebuilt rail in this same journey, such as a new chapter selection. */
  const keepMoment = (moment: StoryMoment | undefined): void => {
    pendingScene.current = { journey, moment };
  };

  const inUniverse = journey === "universe" && universe !== null;
  const loaded = inUniverse ? universe : book;
  // Without spoilers, people there in secret stay off the rail until the story reveals them.
  const active = useMemo(() => {
    if (spoilersShown) {
      return loaded;
    }
    const chapter = withoutSecretPresence(loaded.chapter);
    return chapter === loaded.chapter
      ? loaded
      : journeyModel(chapter, loaded.spans, inUniverse ? "universe" : "book");
  }, [loaded, spoilersShown, inUniverse]);
  // Only the view on screen is worked out. The other waits until someone switches to it.
  const layout = useMemo(
    () => buildBookTimelineLayout(active.chapter, inUniverse ? "universe" : "book"),
    [active.chapter, inUniverse],
  );
  return {
    journey,
    active,
    layout,
    loading,
    failed,
    prefetch,
    requestJourney,
    hasPendingScene,
    keepMoment,
    takePendingMoment,
  };
}
